import { createElement, useCallback, useEffect } from 'react';
import Card from '../../components/Card/Card';
import Container from '../../components/Container/Container';
import Wizard from '../../components/Wizard/Wizard';
import SignUp from '../SignUp';
import Step from '../../components/Wizard/Step';
import './SignUpNativo.scss';
import SignUpProvider, { SignUpContext } from '../../data/SignUp/SignUpContext';
import { useMemo } from 'react';
import PhoneNumber from '../PhoneNumber';
import SuccessSignUp from '../SuccessSignUp';
import EmailPage from '../Email';
import { useLocation, useNavigate } from 'react-router-dom';
import { useControllerFlowStepsContext } from '../../hooks/ControllerFlowStepsContext';
import { IAccountControllerLogin } from '../../interfaces/ISocial';
import SuccessMerge from '../SuccessMerge';
import { useAuthContext } from '../../hooks/AuthContext';
import ValidateEmail from '../ValidateEmail';
import {
  CTV_SUCCESS_PAGE_PATH,
  ORIGIN_DOMAIN_SBT_TV
} from '../../data/Constants';
import {
  getOriginDomain,
  pushGtmEvent,
  shouldOpenMenuAccount
} from '../../utils/GenericFunctions';
import { usePersistedState } from '../../hooks/PreserveState';
import CpfPage from '../Cpf';
import SignUpBirthdate from './SignUpBirthdate';
import SignUpFirstName from './SignUpFirstName';
import SignUpLastName from './SignUpLastName';
import SignUpPassword from './SignUpPassword';
import SignUpSocialName from './SignUpSocialName';
import SocialConnectionError from '../SocialConnectionError';

export default function SignUpNativo() {
  const generateKey = (key: string) => `signup-${key}`;

  const { setSSORedirect, redirectURL } = useAuthContext();
  const { getOrigin, controllerViews, ctvCode, requireCpf } =
    useControllerFlowStepsContext();
  const { redirectToSite } = useAuthContext();
  const navigate = useNavigate();
  const components: any = {
    SignUp: {
      id: 'criar-conta',
      component: SignUp,
      allowSkipStep: false
    },
    SignUpFirstName: {
      id: 'criar-conta/nome',
      component: SignUpFirstName,
      allowSkipStep: false
    },
    SignUpLastName: {
      id: 'criar-conta/sobrenome',
      component: SignUpLastName,
      allowSkipStep: false
    },
    SignUpSocialName: {
      id: 'criar-conta/nome-social',
      component: SignUpSocialName,
      allowSkipStep: false
    },
    SignUpBirthdate: {
      id: 'criar-conta/aniversario',
      component: SignUpBirthdate,
      allowSkipStep: false
    },
    ValidateEmail: {
      id: 'validar-email',
      component: ValidateEmail,
      allowSkipStep: false
    },
    PhoneNumber: {
      id: 'cadastro-telefone',
      component: PhoneNumber,
      allowSkipStep: true
    },
    CpfPage: {
      id: 'cadastro-documento',
      component: CpfPage,
      allowSkipStep: false
    },
    EmailPage: {
      id: 'cadastro-email',
      component: EmailPage,
      allowSkipStep: false
    },
    SignUpPassword: {
      id: 'criar-conta/senha',
      component: SignUpPassword,
      allowSkipStep: false
    },
    SuccessSignUp: {
      id: 'cadastro-concluido',
      component: SuccessSignUp,
      allowSkipStep: false
    },
    SuccessMerge: {
      id: 'cadastro-concluido-merge',
      component: SuccessMerge,
      allowSkipStep: false,
      updateData: false
    },
    SocialConnectionError: {
      id: 'erro-conexao-social',
      component: SocialConnectionError,
      allowSkipStep: false,
      updateData: false
    }
  };
  const [currentStep, setCurrentStep] = usePersistedState<number>(
    generateKey('currentStep'),
    0
  );
  const initialSteps = [
    getComponent('SignUp', false),
    getComponent('EmailPage', false),
    getComponent('SignUpPassword', false),
    getComponent('SignUpFirstName', false),
    getComponent('SignUpLastName', false),
    getComponent('SignUpSocialName', false),
    getComponent('SignUpBirthdate', false),
    ...(requireCpf ? [getComponent('CpfPage', false)] : []),
    getComponent('SuccessSignUp', false)
  ];
  const [defaultSteps, setDefaultSteps] = usePersistedState<any[]>(
    generateKey('defaultSteps'),
    initialSteps,
    {
      isComponent: true,
      componentMap: components
    }
  );
  const handleChangeStep = useCallback((indexStep: number) => {
    setCurrentStep(indexStep);
  }, []);

  useEffect(() => {
    if (currentStep === 1)
      pushGtmEvent('cadastro_ab_test_group', {
        group: requireCpf ? 'com_cpf' : 'sem_cpf'
      });
  }, [currentStep]);

  useEffect(() => {
    const handleLeaveSignUp = () => {
      if (currentStep < defaultSteps.length - 1) {
        pushGtmEvent('cadastro_abandono', {
          step_atual: defaultSteps[currentStep]?.id,
          grupo_ab_teste: requireCpf ? 'com_cpf' : 'sem_cpf'
        });
      }
    };

    window.addEventListener('beforeunload', handleLeaveSignUp);

    return () => {
      window.removeEventListener('beforeunload', handleLeaveSignUp);
    };
  }, [currentStep, defaultSteps, requireCpf]);

  function getComponent(
    key: string,
    allowSkipStep: boolean,
    updateData?: boolean
  ) {
    const component = components[key];
    component['updateData'] = updateData;
    component['allowSkipStep'] = allowSkipStep;
    return component;
  }

  useEffect(() => {
    if (Object.keys(controllerViews).length) {
      controllerViewsPage(controllerViews);
    }
  }, [controllerViews]);

  function controllerViewsPage(data: IAccountControllerLogin) {
    if (data.mergeAccount) {
      if (ctvCode) return navigate(CTV_SUCCESS_PAGE_PATH);
      if (shouldOpenMenuAccount(redirectURL)) return navigate('/menu-de-conta');
      redirectToSite();
    } else {
      const updateSteps = [];

      if (!data.hasEmail) {
        updateSteps.push(getComponent('EmailPage', true));
      }

      // Apenas TV e SBT devem pedir telefone
      if (
        !data.hasTelephone &&
        getOrigin() !== 'videos' &&
        (getOrigin() === '' || getOriginDomain() === ORIGIN_DOMAIN_SBT_TV)
      ) {
        updateSteps.push(getComponent('PhoneNumber', true, true));
      }

      if (updateSteps.length) {
        if (requireCpf) updateSteps.push(getComponent('CpfPage', false));
        updateSteps.push(getComponent('SuccessSignUp', false));
        setDefaultSteps(updateSteps);
      } else {
        if (ctvCode) return navigate(CTV_SUCCESS_PAGE_PATH);
        redirectToSite();
      }
    }
  }

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('redirect')) {
      setSSORedirect(query.get('redirect') as string);
    }
    const indexSuccessMerge = defaultSteps.findIndex(
      (step: any) => step.id === 'cadastro-concluido-merge'
    );
    if (Object.keys(controllerViews).length && indexSuccessMerge > -1) {
      setCurrentStep(indexSuccessMerge);
    }
  }, [defaultSteps]);

  const location = useLocation();
  useEffect(() => {
    if (location?.state?.socialConnectionError) {
      setDefaultSteps([getComponent('SocialConnectionError', false)]);
      setCurrentStep(0);
      return;
    }

    if (location?.state?.createAccountFromSocial) {
      mountFlowcreateAccountFromSocial();
    }
    if (location?.state?.createAccountFromSocialWithoutEmail) {
      mountFlowcreateAccountFromSocialWithoutEmail();
    }
  }, [location, requireCpf]);

  function mountFlowcreateAccountFromSocial() {
    let updateSteps = [];

    updateSteps = [
      getComponent('SignUpFirstName', false),
      getComponent('SignUpLastName', false),
      getComponent('SignUpSocialName', false),
      getComponent('SignUpBirthdate', false),
      ...(requireCpf ? [getComponent('CpfPage', false)] : []),
      getComponent('SuccessSignUp', false)
    ];
    setDefaultSteps(updateSteps);
  }

  function mountFlowcreateAccountFromSocialWithoutEmail() {
    let updateSteps = [];

    updateSteps = [
      getComponent('EmailPage', false),
      getComponent('SignUpFirstName', false),
      getComponent('SignUpLastName', false),
      getComponent('SignUpSocialName', false),
      getComponent('SignUpBirthdate', false),
      ...(requireCpf ? [getComponent('CpfPage', false)] : []),
      getComponent('SuccessSignUp', false)
    ];
    setDefaultSteps(updateSteps);
  }

  const steps = useMemo(() => defaultSteps, [defaultSteps]);

  const buildSteps = useCallback(() => {
    const lastIndex = steps.length - 1;
    return steps.map((step, index) => {
      const component = createElement(step.component, {
        previousPath: index > 0 ? index - 1 : null,
        nextPath: index < lastIndex ? index + 1 : null,
        allowSkipStep: step.allowSkipStep,
        updateData: step.updateData,
        handleChangeStep,
        context: SignUpContext
      });
      return (
        <Step id={index} currentStep={currentStep} key={index}>
          {component}
        </Step>
      );
    });
  }, [currentStep, handleChangeStep, steps]);

  return (
    <SignUpProvider>
      <Container
        className={`signup ${steps?.[currentStep]?.id === 'cadastro-concluido' ? 'success' : ''}`}
      >
        <Card width="400px" minHeight="660px" center>
          <form className="width-full" onSubmit={(e) => e.preventDefault()}>
            <Wizard>{buildSteps()}</Wizard>
          </form>
        </Card>
      </Container>
    </SignUpProvider>
  );
}
